import React, { useState } from 'react'
import classNames from 'classnames'
import { type PredefinedCrossClientQuery } from '../../../../models/CrossClient'
import { useAppSelector } from '../../../../store/hooks'
import { selectCrossClientInitLoading } from '../../../../store/crossClient/crossClientLanding'
import { onActivateKeyDown } from '../../utils'
import { CrossClientRowSkeleton } from '../CrossClientRowSkeleton'
import { CrossClientEmptyState } from '../CrossClientEmptyState'
import './CrossClientLandingPredefinedQueries.scss'

export interface CrossClientLandingPredefinedQueriesProps {
  queries: PredefinedCrossClientQuery[]
  loading: boolean
  isError: boolean
  onSelect: (predefinedQuery: PredefinedCrossClientQuery) => void
  runDisabled?: boolean
}

export const CrossClientLandingPredefinedQueries: React.FC<CrossClientLandingPredefinedQueriesProps> = ({
  queries,
  loading,
  isError,
  onSelect,
  runDisabled,
}) => {
  const initLoading = useAppSelector(selectCrossClientInitLoading)

  if (loading) {
    return <CrossClientRowSkeleton count={6} />
  }

  if (isError) {
    return <CrossClientEmptyState message="Couldn't load recommended queries. Please try again." />
  }

  if (!queries.length) {
    return <CrossClientEmptyState message='No recommended queries' />
  }

  return (
    <div className='d-grid gap-3 align-items-start w-100 cross-client-predefined-grid'>
      {queries.map((item) => (
        <CrossClientLandingPredefinedQueryCard key={item.id} predefinedQuery={item} initLoading={initLoading} runDisabled={runDisabled} onClick={onSelect} />
      ))}
    </div>
  )
}

interface CrossClientLandingPredefinedQueryCardProps {
  predefinedQuery: PredefinedCrossClientQuery
  initLoading: boolean
  runDisabled?: boolean
  onClick: (predefinedQuery: PredefinedCrossClientQuery) => void
}

const CrossClientLandingPredefinedQueryCard: React.FC<CrossClientLandingPredefinedQueryCardProps> = ({
  predefinedQuery,
  initLoading,
  runDisabled,
  onClick,
}) => {
  const isDisabled = runDisabled || initLoading
  const [hovered, setHovered] = useState<boolean>(false)

  const category = predefinedQuery.category
  const { textColor, iconBackgroundColor, iconBorderColor, icon } = category.metadata
  const hasBootstrapBaseClass = icon.value.split(' ').includes('bi')
  const iconClassName = hasBootstrapBaseClass ? icon.value : `bi ${icon.value}`

  return (
    <div
      className={classNames(
        'd-flex flex-column align-items-start gap-2 p-3 zocks-border-0.75 border bg-white h-100 cross-client-transition cross-client-predefined-card',
        {
          'zocks-border-color-gray-200': !hovered || isDisabled,
          'zocks-border-color-primary cross-client-predefined-card-box-shadow': hovered && !isDisabled,
          'opacity-50 cross-client-predefined-card-disabled': isDisabled,
        },
      )}
      role={isDisabled ? undefined : 'button'}
      tabIndex={isDisabled ? undefined : 0}
      onClick={() => {
        if (isDisabled) {
          return
        }
        onClick(predefinedQuery)
      }}
      onKeyDown={isDisabled ? undefined : onActivateKeyDown(() => onClick(predefinedQuery))}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className='d-flex gap-2 align-items-center'>
        <div
          className='d-flex align-items-center justify-content-center cross-client-predefined-card-icon'
          style={{ backgroundColor: iconBackgroundColor, borderColor: iconBorderColor }}
        >
          {icon.type === 'svgUrl' ? (
            <img src={icon.value} alt='' aria-hidden='true' width='16' height='16' loading='lazy' referrerPolicy='no-referrer' />
          ) : (
            <i className={iconClassName} style={{ color: textColor }} aria-hidden='true' />
          )}
        </div>
        <div className='paragraph-small medium' style={{ color: textColor }}>
          {predefinedQuery.title}
        </div>
      </div>
      <div className='paragraph-small zocks-text-gray-900'>{predefinedQuery.description}</div>
    </div>
  )
}
